import type { PartnerStructuredColumnDto } from '@artic/api-client';
import {
  addDays,
  addMonths,
  addYears,
  differenceInCalendarDays,
  format,
  getISOWeek,
  getWeek,
  startOfMonth,
  startOfWeek,
  startOfYear,
} from 'date-fns';

import { formatIsoDate, parseIsoDate } from '../../components/iso-date';
import { isAmericanEnglish, localeDatePatterns } from '../../i18n/date-format';
import type { WeekStartsOn } from '../capacity-plan/types';
import type { ProjectWorkOrderDetail } from './types';

export const projectTimeWindowValues = ['everything', 'year', 'month', 'week', 'day'] as const;

export type ProjectTimeWindow = (typeof projectTimeWindowValues)[number];

export interface ProjectPlanPeriod {
  /** First calendar day in the period, inclusive. */
  start: string;
  /** First calendar day after the period, exclusive. */
  end: string;
}

type ProjectPlanWorkOrder = Pick<
  ProjectWorkOrderDetail,
  | 'work_order_current_status'
  | 'work_order_scheduled_start'
  | 'work_order_scheduled_complete'
  | 'work_order_area_value'
  | 'work_order_area_unit'
  | 'work_order_length_value'
  | 'work_order_length_unit'
>;

export interface ProjectThroughputMetric {
  key: 'area' | 'length';
  /** Partner-authored Structured Column label — data, never localised. */
  label: string;
  unit: string | null;
  value: number;
  /** Null when the project dates needed for the divisor are unavailable. */
  perWeek: number | null;
  /** Week already displays a weekly aggregate, so its secondary value is hidden. */
  showPerWeek: boolean;
}

export interface ProjectPlanSummary {
  workOrderCount: number;
  throughput: ProjectThroughputMetric[];
}

function toCalendarDay(value: string | null | undefined): string | null {
  if (!value) return null;
  const day = value.slice(0, 10);
  return parseIsoDate(day) ? day : null;
}

function toIso(date: Date): string {
  return formatIsoDate(date);
}

/**
 * Week-of-year for the period label. `getISOWeek` is Monday-only by
 * definition, so a Sunday-start partner would see the wrong number: their week
 * beginning Sunday D falls at the END of ISO week N, while its Mon–Sat body
 * sits in ISO week N+1 — the label would lag the window by one every time.
 * `getWeek` with the partner's own `weekStartsOn` numbers the week the way
 * that partner actually counts it. Monday-start keeps exact ISO semantics
 * (`firstWeekContainsDate: 4`); Sunday-start uses the US convention of
 * "week 1 contains 1 January".
 */
export function weekNumber(date: Date, weekStartsOn: WeekStartsOn): number {
  return weekStartsOn === 1
    ? getISOWeek(date)
    : getWeek(date, { weekStartsOn, firstWeekContainsDate: 1 });
}

export function projectPlanPeriod(
  anchorIso: string,
  timeWindow: ProjectTimeWindow,
  weekStartsOn: WeekStartsOn,
): ProjectPlanPeriod | null {
  if (timeWindow === 'everything') return null;
  const anchor = parseIsoDate(anchorIso);
  if (!anchor) return null;

  switch (timeWindow) {
    case 'day': {
      const start = toIso(anchor);
      return { start, end: toIso(addDays(anchor, 1)) };
    }
    case 'week': {
      const startDate = startOfWeek(anchor, { weekStartsOn });
      return { start: toIso(startDate), end: toIso(addDays(startDate, 7)) };
    }
    case 'month': {
      const startDate = startOfMonth(anchor);
      return { start: toIso(startDate), end: toIso(addMonths(startDate, 1)) };
    }
    case 'year': {
      const startDate = startOfYear(anchor);
      return { start: toIso(startDate), end: toIso(addYears(startDate, 1)) };
    }
  }
}

export function stepProjectPlanPeriod(
  anchorIso: string,
  timeWindow: Exclude<ProjectTimeWindow, 'everything'>,
  direction: -1 | 1,
  weekStartsOn: WeekStartsOn,
): string {
  const period = projectPlanPeriod(anchorIso, timeWindow, weekStartsOn);
  const start = period ? parseIsoDate(period.start) : null;
  if (!start) return anchorIso;

  switch (timeWindow) {
    case 'day':
      return toIso(addDays(start, direction));
    case 'week':
      return toIso(addDays(start, direction * 7));
    case 'month':
      return toIso(addMonths(start, direction));
    case 'year':
      return toIso(addYears(start, direction));
  }
}

export interface ProjectPlanPeriodParts {
  /** Week-of-year for the Week window, `null` for every other resolution. */
  weekNumber: number | null;
  /** The period's date, ordered for the given locale. */
  date: string;
}

/**
 * The period label's *parts*, not its sentence. "Week" is copy and lives in the
 * `project` namespace, so this returns the number and the formatted date and
 * lets the render site assemble them — a formatted date must never end up
 * inside a locale bundle.
 */
export function projectPlanPeriodParts(
  period: ProjectPlanPeriod,
  timeWindow: Exclude<ProjectTimeWindow, 'everything'>,
  weekStartsOn: WeekStartsOn,
  language: string | undefined,
): ProjectPlanPeriodParts {
  const start = parseIsoDate(period.start);
  if (!start) return { weekNumber: null, date: period.start };
  const patterns = localeDatePatterns(language);

  switch (timeWindow) {
    case 'day':
      return { weekNumber: null, date: format(start, patterns.weekdayShortMonth) };
    case 'week':
      return {
        weekNumber: weekNumber(start, weekStartsOn),
        date: format(start, patterns.shortMonth),
      };
    case 'month':
      return { weekNumber: null, date: format(start, 'MMMM yyyy') };
    case 'year':
      return { weekNumber: null, date: format(start, 'yyyy') };
  }
}

/**
 * The date (or, for Week, the date range) the empty-state sentence is about,
 * ordered for the given locale. `null` when the period's dates don't parse — the
 * render site then falls back to the resolution-only sentence.
 */
export function projectPlanEmptyStatePeriod(
  period: ProjectPlanPeriod,
  timeWindow: Exclude<ProjectTimeWindow, 'everything'>,
  language: string | undefined,
): string | null {
  const start = parseIsoDate(period.start);
  const endExclusive = parseIsoDate(period.end);
  if (!start || !endExclusive) return null;
  const patterns = localeDatePatterns(language);

  if (timeWindow === 'week') {
    const end = addDays(endExclusive, -1);
    const sameMonth =
      start.getMonth() === end.getMonth() && start.getFullYear() === end.getFullYear();
    // Within one month only the day needs repeating on the left of the dash —
    // "13th-19th July 2026" rather than restating the month twice. American
    // ordering puts the month first, so that shortcut doesn't apply there.
    return sameMonth && !isAmericanEnglish(language)
      ? `${format(start, 'do')}-${format(end, patterns.ordinalLongMonth)}`
      : `${format(start, patterns.ordinalShortMonth)}-${format(end, patterns.ordinalShortMonth)}`;
  }

  switch (timeWindow) {
    case 'day':
      return format(start, patterns.ordinalLongMonth);
    case 'month':
      return format(start, 'MMMM yyyy');
    case 'year':
      return format(start, 'yyyy');
  }
}

export function isDateInProjectPlanPeriod(
  value: string | null | undefined,
  period: ProjectPlanPeriod | null,
): boolean {
  if (period === null) return true;
  const day = toCalendarDay(value);
  return day !== null && day >= period.start && day < period.end;
}

export function startsInProjectPlanPeriod(
  workOrder: Pick<ProjectPlanWorkOrder, 'work_order_scheduled_start'>,
  period: ProjectPlanPeriod | null,
): boolean {
  return isDateInProjectPlanPeriod(workOrder.work_order_scheduled_start, period);
}

function completesInProjectPlanPeriod(
  workOrder: Pick<ProjectPlanWorkOrder, 'work_order_scheduled_complete'>,
  period: ProjectPlanPeriod | null,
): boolean {
  return isDateInProjectPlanPeriod(workOrder.work_order_scheduled_complete, period);
}

const SQFT_PER_M2 = 10.7639;

function finiteNumber(value: string | null): number | null {
  if (value === null || value.trim() === '') return null;
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : null;
}

function convertArea(value: string | null, sourceUnit: string | null, targetUnit: string): number {
  const numeric = finiteNumber(value);
  if (numeric === null || sourceUnit === null) return 0;
  if (sourceUnit === targetUnit) return numeric;
  if (sourceUnit === 'm2' && targetUnit === 'sqft') return numeric * SQFT_PER_M2;
  if (sourceUnit === 'sqft' && targetUnit === 'm2') return numeric / SQFT_PER_M2;
  return 0;
}

const METRIC_LENGTH_UNITS = new Set(['mm', 'cm', 'm']);
const IMPERIAL_LENGTH_UNITS = new Set(['inch', 'ft', 'yd']);

function convertLength(
  value: string | null,
  sourceUnit: string | null,
  targetUnit: string,
): number {
  const numeric = finiteNumber(value);
  if (numeric === null || sourceUnit === null) return 0;

  // Length values are stored canonically as millimetres for metric rows and
  // inches for imperial rows. The companion unit records the user's display
  // unit, which tells us which canonical system the numeric value belongs to.
  const metres = METRIC_LENGTH_UNITS.has(sourceUnit)
    ? numeric / 1000
    : IMPERIAL_LENGTH_UNITS.has(sourceUnit)
      ? numeric * 0.0254
      : null;
  if (metres === null) return 0;

  switch (targetUnit) {
    case 'mm':
      return metres * 1000;
    case 'cm':
      return metres * 100;
    case 'm':
      return metres;
    case 'inch':
      return metres / 0.0254;
    case 'ft':
      return metres / (0.0254 * 12);
    case 'yd':
      return metres / (0.0254 * 36);
    default:
      return 0;
  }
}

function projectWeeksWithinPeriod(
  projectStart: string | null,
  projectEnd: string | null,
  period: ProjectPlanPeriod | null,
): number | null {
  const start = toCalendarDay(projectStart);
  const end = toCalendarDay(projectEnd);
  if (!start || !end || end < start) return null;

  const endDate = parseIsoDate(end);
  if (!endDate) return null;
  const projectEndExclusive = toIso(addDays(endDate, 1));
  const overlapStart = period && period.start > start ? period.start : start;
  const overlapEnd = period && period.end < projectEndExclusive ? period.end : projectEndExclusive;

  // A valid project can still sit wholly outside the selected Month/Year. In
  // that case there is no occupied project week to divide by; the UI renders
  // the weekly rate as unavailable rather than inventing a calendar week.
  if (overlapStart >= overlapEnd) return null;
  const overlapStartDate = parseIsoDate(overlapStart);
  const overlapEndDate = parseIsoDate(overlapEnd);
  if (!overlapStartDate || !overlapEndDate) return null;
  const days = differenceInCalendarDays(overlapEndDate, overlapStartDate);
  return Math.max(1, Math.ceil(days / 7));
}

function configuredMetric(
  structuredColumns: readonly PartnerStructuredColumnDto[],
  key: 'area' | 'length',
): PartnerStructuredColumnDto | null {
  return structuredColumns.find((column) => column.key === key && column.is_enabled) ?? null;
}

export function calculateProjectPlanSummary({
  workOrders,
  structuredColumns,
  projectStart,
  projectEnd,
  timeWindow,
  period,
  weekStartsOn,
}: {
  workOrders: readonly ProjectPlanWorkOrder[];
  structuredColumns: readonly PartnerStructuredColumnDto[];
  projectStart: string | null;
  projectEnd: string | null;
  timeWindow: ProjectTimeWindow;
  period: ProjectPlanPeriod | null;
  /** Partner's first day of week; the Day window's per-week rate buckets by it. */
  weekStartsOn: WeekStartsOn;
}): ProjectPlanSummary {
  const activeWorkOrders = workOrders.filter(
    (workOrder) => workOrder.work_order_current_status !== 'archived',
  );
  const workOrderCount = activeWorkOrders.filter((workOrder) =>
    startsInProjectPlanPeriod(workOrder, period),
  ).length;
  const throughputRows = activeWorkOrders.filter((workOrder) =>
    completesInProjectPlanPeriod(workOrder, period),
  );

  const areaColumn = configuredMetric(structuredColumns, 'area');
  const lengthColumn = configuredMetric(structuredColumns, 'length');
  const areaUnit = areaColumn?.default_unit ?? areaColumn?.allowed_units?.[0] ?? 'm2';
  const lengthUnit = lengthColumn?.default_unit ?? lengthColumn?.allowed_units?.[0] ?? 'm';

  const metricDefinitions: {
    key: ProjectThroughputMetric['key'];
    label: string;
    unit: string | null;
    total: (rows: readonly ProjectPlanWorkOrder[]) => number;
  }[] = [
    ...(areaColumn
      ? [
          {
            key: 'area' as const,
            label: areaColumn.label,
            unit: areaUnit,
            total: (rows: readonly ProjectPlanWorkOrder[]) =>
              rows.reduce(
                (sum, row) =>
                  sum + convertArea(row.work_order_area_value, row.work_order_area_unit, areaUnit),
                0,
              ),
          },
        ]
      : []),
    ...(lengthColumn
      ? [
          {
            key: 'length' as const,
            label: lengthColumn.label,
            unit: lengthUnit,
            total: (rows: readonly ProjectPlanWorkOrder[]) =>
              rows.reduce(
                (sum, row) =>
                  sum +
                  convertLength(
                    row.work_order_length_value,
                    row.work_order_length_unit,
                    lengthUnit,
                  ),
                0,
              ),
          },
        ]
      : []),
  ];

  const projectWeeks = projectWeeksWithinPeriod(projectStart, projectEnd, period);
  const containingWeek =
    timeWindow === 'day' && period ? projectPlanPeriod(period.start, 'week', weekStartsOn) : null;
  const containingWeekRows = containingWeek
    ? activeWorkOrders.filter((workOrder) =>
        completesInProjectPlanPeriod(workOrder, containingWeek),
      )
    : [];

  return {
    workOrderCount,
    throughput: metricDefinitions.map((metric) => {
      const value = metric.total(throughputRows);
      const perWeek =
        timeWindow === 'week'
          ? null
          : timeWindow === 'day'
            ? metric.total(containingWeekRows)
            : projectWeeks === null
              ? null
              : value / projectWeeks;
      return {
        key: metric.key,
        label: metric.label,
        unit: metric.unit,
        value,
        perWeek,
        showPerWeek: timeWindow !== 'week',
      };
    }),
  };
}
